import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_drive-smart-25/artifacts/vqfz3t07_ENFLEET%20by%20POMEN.png";

function FlowyBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-white/5 blur-3xl" />
      <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-[#4f46e5]/20 blur-3xl" />
      <div className="absolute -bottom-40 left-1/4 w-[550px] h-[550px] rounded-full bg-[#2a17a8]/40 blur-3xl" />
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ height: "38vh" }}>
        <path fill="rgba(255,255,255,0.05)" d="M0,224L60,208C120,192,240,160,360,165.3C480,171,600,213,720,224C840,235,960,213,1080,186.7C1200,160,1320,128,1380,112L1440,96L1440,320L0,320Z" />
        <path fill="rgba(255,255,255,0.04)" d="M0,288L80,272C160,256,320,224,480,218.7C640,213,800,235,960,245.3C1120,256,1280,256,1360,256L1440,256L1440,320L0,320Z" />
      </svg>
      <svg className="absolute top-0 right-0 w-full rotate-180" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ height: "26vh" }}>
        <path fill="rgba(255,255,255,0.04)" d="M0,224L60,208C120,192,240,160,360,165.3C480,171,600,213,720,224C840,235,960,213,1080,186.7C1200,160,1320,128,1380,112L1440,96L1440,320L0,320Z" />
      </svg>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("auth"); // auth | forgot
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [ssoConfigured, setSsoConfigured] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get("/auth/mydigitalid/status").then((r) => setSsoConfigured(r.data.configured)).catch(() => {});
    const ssoErr = searchParams.get("sso_error");
    if (ssoErr) setError(ssoErr);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (path, body) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(path, body);
      login(data.token, data.user);
      navigate("/");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotMsg(data.message);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#10056E] flex flex-col items-center justify-center p-6">
      <FlowyBackground />
      <div className="relative z-10 w-full max-w-md fade-up">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="ENFLEET by POMEN" className="h-14 w-auto" data-testid="login-logo" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-7 sm:p-8">
          {mode === "forgot" ? (
            <div data-testid="forgot-password-panel">
              <h2 className="font-heading text-xl font-semibold tracking-tight mb-1">Lupa Kata Laluan</h2>
              <p className="text-sm text-muted-foreground mb-6">Masukkan emel berdaftar anda — kami akan hantar pautan set semula.</p>
              {forgotMsg ? (
                <div className="space-y-4">
                  <p className="text-sm bg-green-50 border border-green-200 text-[#146c43] rounded-lg p-3" data-testid="forgot-success-msg">{forgotMsg}</p>
                  <Button variant="outline" className="w-full" onClick={() => { setMode("auth"); setForgotMsg(""); }} data-testid="back-to-login-btn">
                    Kembali ke Log Masuk
                  </Button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={submitForgot}>
                  <div className="space-y-1.5">
                    <Label>Emel</Label>
                    <Input data-testid="forgot-email-input" type="email" required value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)} placeholder="nama@agensi.gov.my" />
                  </div>
                  {error && <p className="text-sm text-[#DC3545]" data-testid="auth-error">{error}</p>}
                  <Button type="submit" className="w-full bg-[#10056E] hover:bg-[#2a17a8]" disabled={busy} data-testid="forgot-submit-btn">
                    {busy ? "Menghantar..." : "Hantar Pautan Set Semula"}
                  </Button>
                  <button type="button" onClick={() => { setMode("auth"); setError(""); }} data-testid="forgot-cancel-btn"
                    className="w-full text-center text-sm text-[#10056E] hover:underline font-medium">
                    Kembali ke Log Masuk
                  </button>
                </form>
              )}
            </div>
          ) : (
            <div data-testid="login-panel">
              <h2 className="font-heading text-xl font-semibold tracking-tight mb-6 text-center">Log Masuk</h2>
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit("/auth/login", loginForm);
                  }}
                >
                  <div className="space-y-1.5">
                    <Label>Emel</Label>
                    <Input data-testid="login-email-input" type="email" required value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="nama@agensi.gov.my" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Kata Laluan</Label>
                    <Input data-testid="login-password-input" type="password" required value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                  </div>
                  <div className="flex justify-end -mt-1">
                    <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link"
                      className="text-xs text-[#10056E] hover:underline font-medium">
                      Lupa kata laluan?
                    </button>
                  </div>
                  {error && <p className="text-sm text-[#DC3545]" data-testid="auth-error">{error}</p>}
                  <Button type="submit" className="w-full bg-[#10056E] hover:bg-[#2a17a8]" disabled={busy} data-testid="login-submit-button">
                    {busy ? "Sedang diproses..." : "Log Masuk"}
                  </Button>
                  <div className="flex items-center gap-3 py-1">
                    <span className="h-px bg-slate-200 flex-1" />
                    <span className="text-[11px] text-muted-foreground uppercase tracking-wide">atau</span>
                    <span className="h-px bg-slate-200 flex-1" />
                  </div>
                  <Button type="button" variant="outline" disabled={!ssoConfigured}
                    className="w-full border-slate-300 font-semibold"
                    data-testid="mydigitalid-login-btn"
                    title={ssoConfigured ? "Log masuk menggunakan MyDigital ID" : "MyDigital ID SSO belum diaktifkan oleh agensi"}
                    onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/mydigitalid/login`; }}>
                    <span className="h-5 w-5 rounded bg-gradient-to-br from-[#10056E] to-[#4f46e5] text-white text-[9px] font-black flex items-center justify-center mr-2">ID</span>
                    Log Masuk dengan MyDigital ID
                  </Button>
                  {!ssoConfigured && (
                    <p className="text-[10px] text-muted-foreground text-center -mt-1">MyDigital ID SSO belum diaktifkan untuk agensi ini.</p>
                  )}
                  <div className="text-center text-sm pt-1">
                    <p className="text-slate-600">Adakah anda Agensi Kerajaan Malaysia?</p>
                    <Link to="/daftar" data-testid="register-interest-link"
                      className="inline-block text-[#10056E] hover:underline font-semibold mt-0.5">
                      Daftar di sini.
                    </Link>
                  </div>
                </form>
            </div>
          )}
        </div>
        <p className="text-center text-white/50 text-xs mt-6">© {new Date().getFullYear()} ENFLEET by POMEN</p>
      </div>
    </div>
  );
}
